/**
 * The optional AI assistant.
 *
 * Off unless the server has both `AI_ASSISTANT_ENABLED` and an API key. Every screen asks
 * `useAssistantEnabled()` first and renders nothing when it is false -- including when the
 * status call itself fails, because an assistant that cannot be reached is the same, to a
 * reader, as one that is switched off.
 *
 * Every answer is `{ enabled, text }` and `text` may be null: the model was unreachable,
 * refused, or today's lid was reached. That is a normal answer, shown as a quiet line,
 * never an error toast.
 */

import { useQuery } from '@tanstack/react-query'

import { API_PREFIX, apiGet } from '@/api/client'

const BASE = `${API_PREFIX}/assistant`

export interface AssistantText {
  enabled: boolean
  text: string | null
}

export const getAssistantStatus = () => apiGet<{ enabled: boolean }>(`${BASE}/status`)

export const suggestReviewImprovements = (reviewId: string) =>
  apiGet<AssistantText>(`${BASE}/reviews/${reviewId}/suggestions`, { method: 'POST' })

export const analyticsInsights = (days: number) =>
  apiGet<AssistantText>(`${BASE}/analytics/insights?days=${days}`, { method: 'POST' })

export const explainOrderPrice = (orderId: string, itemId: string) =>
  apiGet<AssistantText>(`${BASE}/orders/${orderId}/items/${itemId}/explain`, { method: 'POST' })

export const explainInvoicePrice = (invoiceId: string, itemId: string) =>
  apiGet<AssistantText>(`${BASE}/invoices/${invoiceId}/items/${itemId}/explain`, {
    method: 'POST',
  })

/** Whether to show any assistant UI. False while loading and on any failure. */
export function useAssistantEnabled(): boolean {
  const status = useQuery({
    queryKey: ['assistant', 'status'],
    queryFn: getAssistantStatus,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  return status.data?.enabled === true
}
